import { DestroyRef, Injectable, InjectionToken, inject } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LAZY_CHUNK_RECOVERY_STORAGE_KEY } from '@shared/constants/storage-keys.const';
import { WebStorageService } from '@shared/storage/web-storage.service';

export const LAZY_CHUNK_PAGE_RELOAD = new InjectionToken<() => void>('LAZY_CHUNK_PAGE_RELOAD', {
  providedIn: 'root',
  factory: () => () => globalThis.location.reload(),
});

const LAZY_CHUNK_ERROR_MARKERS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
  'chunkloaderror',
  'loading chunk',
] as const;

function isLazyChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  const normalized = message.toLowerCase();
  return LAZY_CHUNK_ERROR_MARKERS.some((marker) => normalized.includes(marker));
}

/**
 * Recovers once from a lazy-route bundle that disappeared during a rolling frontend deployment.
 *
 * A browser tab or Capacitor WebView can keep the previous main bundle alive while the server
 * atomically swaps to a new image. Navigating afterwards makes that old bundle request a lazy
 * chunk hash that no longer exists. Reloading fetches the new, non-cacheable index.html and its
 * matching bundle manifest. The session marker prevents an unavailable or corrupt deployment from
 * entering a reload loop.
 */
@Injectable({ providedIn: 'root' })
export class LazyRouteChunkRecoveryService {
  private readonly router = inject(Router);
  private readonly storage = inject(WebStorageService);
  private readonly reloadPage = inject(LAZY_CHUNK_PAGE_RELOAD);
  private readonly destroyRef = inject(DestroyRef);
  private started = false;

  public start(): void {
    if (this.started) return;
    this.started = true;

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.clearRecoveryMarker();
          return;
        }

        if (event instanceof NavigationError && isLazyChunkLoadError(event.error)) {
          this.reloadOnce(event.url);
        }
      });
  }

  private reloadOnce(failedUrl: string): void {
    try {
      if (this.storage.getItem(LAZY_CHUNK_RECOVERY_STORAGE_KEY, 'session')) return;
      this.storage.setItem(LAZY_CHUNK_RECOVERY_STORAGE_KEY, failedUrl, 'session');
    } catch {
      // Without sessionStorage there is no durable loop guard, so an automatic reload is unsafe.
      return;
    }

    this.reloadPage();
  }

  private clearRecoveryMarker(): void {
    try {
      this.storage.removeItem(LAZY_CHUNK_RECOVERY_STORAGE_KEY, 'session');
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts. Recovery is optional.
    }
  }
}
